import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { authApi } from "@/api/services";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/auth/AuthShell";
import { validatePassword, PASSWORD_HINT } from "@/lib/passwordPolicy";

/**
 * Consumes the emailed password-reset token (`/reset-password?token=...`) and
 * calls POST /auth/reset-password/confirm. The backend reset flow existed but
 * nothing consumed the token — a customer who forgot their password had no way
 * to set a new one. This is the page that closes that loop.
 */
function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setIsLoading(true);
    try {
      await authApi.confirmResetPassword(token, password);
      setIsSuccess(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        (err as { message?: string })?.message;
      setError(
        detail ||
          "Could not reset your password. The link may have expired — request a new one.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell
        eyebrow="Reset"
        title="This reset link is incomplete"
        subtitle="The link is missing its reset token."
        illustrationTagline="Request a fresh link and try again."
        footer={
          <Link
            to="/forgot-password"
            className="font-medium text-brand-600 transition-colors hover:text-brand-700"
          >
            Request a new link
          </Link>
        }
      >
        <p className="py-4 text-center text-sm text-slate-600 dark:text-slate-300">
          Open the most recent &ldquo;Password Reset Request&rdquo; email and use
          its link, or request a new one.
        </p>
      </AuthShell>
    );
  }

  if (isSuccess) {
    return (
      <AuthShell
        eyebrow="Done"
        title="Password updated"
        subtitle="You can now sign in with your new password."
        illustrationTagline="Back in the flow."
        footer={
          <Link
            to="/login"
            className="font-medium text-brand-600 transition-colors hover:text-brand-700"
          >
            Go to sign in
          </Link>
        }
      >
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600 shadow-[var(--shadow-glow)] dark:bg-brand-900/60 dark:text-brand-300">
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Redirecting you to sign in&hellip;
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow="Reset"
      title="Set a new password"
      subtitle="Choose a strong password with at least 12 characters."
      illustrationTagline="Almost done."
      footer={
        <Link
          to="/login"
          className="font-medium text-brand-600 transition-colors hover:text-brand-700"
        >
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="new-password">New password</Label>
          <Input
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={12}
            placeholder="At least 12 characters"
          />
          <p className="text-xs text-muted-foreground">{PASSWORD_HINT}</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm-password">Confirm password</Label>
          <Input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </div>
        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? "Saving…" : "Reset password"}
        </Button>
      </form>
    </AuthShell>
  );
}

export default ResetPassword;
